import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  FormController,
  FormControllerCheckbox,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiInfoToolTip,
  MuiSelect,
  MuiSelectPhoneWithTextInput,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
} from "../../../../../../../../components";
import { MAX_LENGTH_100 } from "../../../../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../../../../helpers/constants/siteCoreKeys";
import {
  emailValidation,
  isNotEmpty,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  ParticipantTabFormKey,
  ParticipantTabFormType,
} from "../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

interface ParticipantContactAndNotificationFormProps {
  pageId: string;
  isEditProgram?: boolean;
  editProgramParticipantData?: ParticipantTabFormType;
  isNextRegister?: boolean;
  smsOptions?: OptionsDataType[];
  emailOptions?: OptionsDataType[];
  isSmsSelected?: boolean;
  isEmailSelected?: boolean;
}

export const ParticipantContactAndNotification: FC<
  ParticipantContactAndNotificationFormProps
> = ({
  pageId,
  isEditProgram,
  editProgramParticipantData,
  isNextRegister = false,
  smsOptions = [],
  emailOptions = [],
  isSmsSelected = false,
  isEmailSelected = false,
}) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext();

  const location = useLocation();
  const isFromNIS = location.state?.isFromNISParticipant ?? false;

  const getLabel = (key: string) => {
    return fetchLabel(
      programSiteCoreKeys.contactAndNotificationPreferences,
      key,
    );
  };

  const getDropdownValues = (key: string) => {
    return fetchDropdownValues(
      programSiteCoreKeys.contactAndNotificationPreferences,
      key,
    );
  };
  const { loggedInUser } = useGetLoggedInUserDetails();

  const getCountryIsoCode = (key: string) => {
    const defaultCountryCode = loggedInUser?.countryCode ?? "";
    const value = getValues(key);
    return isNotEmpty(value) && (isEditProgram || isFromNIS)
      ? value
      : defaultCountryCode;
  };

  const phoneValues = useMemo(() => {
    const { primaryNumber, alternativeNumber, appendPhoneNumber } = getValues();
    return { primaryNumber, alternativeNumber, appendPhoneNumber };
  }, [editProgramParticipantData]);

  const renderEmail = () => (
    <MuiBox
      className="sponsoring-employer-details"
      data-testid="sponsoring-employer"
    >
      <MuiTypography
        component="h2"
        className="sub-label-style"
        variant="h2Medium"
      >
        {getLabel(programSiteCoreKeys.emailSubSection)}
      </MuiTypography>

      {/* Primary Email Section */}
      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.emailType1}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.emailIDType1)}
              options={getDropdownValues(programSiteCoreKeys.emailIDTypeList)}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.emailType1}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.primaryEmail}
          >
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.primaryEmail}`}
              label={getLabel(programSiteCoreKeys.primaryEmailID)}
              placeholder={commonLabels.emailPlaceHolder}
              type="email"
              slotProps={{
                htmlInput: {
                  ...emailValidation,
                  "aria-invalid": !!errors.primaryEmail,
                },
              }}
              maxLength={MAX_LENGTH_100}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>

      {/* Alternative Email Section */}
      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.emailType2}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.emailIDType2)}
              options={getDropdownValues(programSiteCoreKeys.emailIDTypeList)}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.emailType2}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.alternativeEmail}
          >
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.alternativeEmail}`}
              label={getLabel(programSiteCoreKeys.alternateEmailID)}
              placeholder={commonLabels.emailPlaceHolder}
              type="email"
              slotProps={{
                htmlInput: {
                  ...emailValidation,
                  "aria-invalid": !!errors.alternativeEmail,
                },
              }}
              maxLength={MAX_LENGTH_100}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );

  const renderPhoneNumber = () => (
    <MuiBox
      className="sponsoring-employer-details"
      data-testid="sponsoring-employer"
    >
      <MuiTypography
        component="h2"
        className="sub-label-style"
        variant="h2Medium"
      >
        {getLabel(programSiteCoreKeys.phoneSubSection)}
      </MuiTypography>

      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.concatType1}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.contactType1)}
              options={getDropdownValues(
                programSiteCoreKeys.phoneCotactTypeList,
              )}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.concatType1}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.primaryNumber}
          >
            <MuiSelectPhoneWithTextInput
              label={getLabel(programSiteCoreKeys.primaryPhoneNumber)}
              defaultSelectedCountry={getCountryIsoCode(
                ParticipantTabFormKey.primaryIsoCode,
              )}
              defaultValue={
                isEditProgram || isFromNIS ? phoneValues.primaryNumber : ""
              }
              placeholder={commonLabels.telephonePlaceHolder}
              hideFormattingAndDisableValidation={isEditProgram}
              phoneNumberFormId={ParticipantTabFormKey.primaryNumber}
              countryCodeFormId={ParticipantTabFormKey.primaryCountryCode}
              countryIsoCodeFormId={ParticipantTabFormKey.primaryIsoCode}
              searchPlaceholder={commonLabels.search}
              searchNotFound={commonLabels.noResult}
              selectLabel={commonLabels.selectPlaceHolder}
              invalidPhoneNumberMessage={
                isEditProgram
                  ? commonValidationMessages.phoneNumberMinimumOf3DigitsRequiredMessage
                  : commonValidationMessages.invalidPhoneNumber
              }
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.concatType2}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.contactType2)}
              options={getDropdownValues(
                programSiteCoreKeys.phoneCotactTypeList,
              )}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.concatType2}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.alternativeNumber}
          >
            <MuiSelectPhoneWithTextInput
              label={getLabel(programSiteCoreKeys.alternatePhoneNumber)}
              defaultSelectedCountry={getCountryIsoCode(
                ParticipantTabFormKey.alternativeIsoCode,
              )}
              defaultValue={isEditProgram ? phoneValues.alternativeNumber : ""}
              placeholder={commonLabels.telephonePlaceHolder}
              hideFormattingAndDisableValidation={isEditProgram}
              phoneNumberFormId={ParticipantTabFormKey.alternativeNumber}
              countryCodeFormId={ParticipantTabFormKey.alternativeCountryCode}
              countryIsoCodeFormId={ParticipantTabFormKey.alternativeIsoCode}
              searchPlaceholder={commonLabels.search}
              searchNotFound={commonLabels.noResult}
              selectLabel={commonLabels.selectPlaceHolder}
              invalidPhoneNumberMessage={
                isEditProgram
                  ? commonValidationMessages.phoneNumberMinimumOf3DigitsRequiredMessage
                  : commonValidationMessages.invalidPhoneNumber
              }
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );

  const renderNextSection = () => (
    <>
      <MuiBox className="select-preference-authorized">
        <MuiTypography
          id="id-group-label"
          component={"h2"}
          className="career-goal-label-style"
          variant={"h2Bold"}
        >
          {getLabel(programSiteCoreKeys.nextNotificationPreferencesSection)}
        </MuiTypography>
        <MuiInfoToolTip
          isRemoveCloseIcon
          message={getLabel(programSiteCoreKeys.popUpInfo)}
        />
      </MuiBox>
      <MuiBox className="divider-container"></MuiBox>
      <MuiBox
        role="group"
        aria-labelledby="id-group-label"
        className="select-preference-contain"
      >
        <MuiGrid
          role="list"
          container
          className="checkbox-container-career-goal"
        >
          <MuiGrid
            size={gridSize}
            className={gridSizeMediumClass}
            role="listitem"
          >
            <FormControllerCheckbox
              control={control}
              name={ParticipantTabFormKey.smsCheckBox}
            >
              <MuiCheckbox
                checkboxLabel={getLabel(
                  programSiteCoreKeys.smsNotificationsCheckBox,
                )}
                id={`${pageId}-${ParticipantTabFormKey.smsCheckBox}`}
                data-testid={`${pageId}-${ParticipantTabFormKey.smsCheckBox}`}
                disabled
              />
            </FormControllerCheckbox>
          </MuiGrid>
          <MuiGrid
            size={gridSize}
            className={gridSizeMediumClass}
            role="listitem"
          >
            <FormControllerCheckbox
              control={control}
              name={ParticipantTabFormKey.emailCheckBox}
            >
              <MuiCheckbox
                checkboxLabel={getLabel(
                  programSiteCoreKeys.emailNotificationsCheckBox,
                )}
                id={`${pageId}-${ParticipantTabFormKey.emailCheckBox}`}
                data-testid={`${pageId}-${ParticipantTabFormKey.emailCheckBox}`}
                disabled
              />
            </FormControllerCheckbox>
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
      <MuiBox className="sponsoring-employer-details">
        <MuiGrid container className="main-container">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.nextSms}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.sendSmsNotificationsTo)}
                options={smsOptions}
                value=""
                disabled={!isSmsSelected}
                id={`${pageId}-${ParticipantTabFormKey.nextSms}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.nextEmail}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.sendEmailNotificationsTo)}
                options={emailOptions}
                disabled={!isEmailSelected}
                value=""
                id={`${pageId}-${ParticipantTabFormKey.nextEmail}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
      <MuiBox className="select-preference-authorized">
        <MuiTypography
          id="id-group-label"
          component={"h2"}
          className="career-goal-label-style"
          variant={"h2Bold"}
        >
          {getLabel(
            programSiteCoreKeys.additionalParticipantContactDetailsSection,
          )}
        </MuiTypography>
        <MuiInfoToolTip
          isRemoveCloseIcon
          message={""} //message will cover later
        />
      </MuiBox>
      <MuiBox className="divider-container"></MuiBox>
    </>
  );

  return (
    <MuiBox>
      {isNextRegister && renderNextSection()}
      {renderEmail()}
      {renderPhoneNumber()}
      <MuiBox className="select-preference-container">
        <MuiBox
          className="sponsoring-employer-details"
          data-testid="sponsoring-employer"
        >
          <MuiGrid container className="main-container">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={ParticipantTabFormKey.appendPhoneNumber}
              >
                <MuiSelectPhoneWithTextInput
                  label={getLabel(programSiteCoreKeys.appendedPhoneNumber)}
                  defaultSelectedCountry={getCountryIsoCode(
                    ParticipantTabFormKey.appendPhoneNumberIsoCode,
                  )}
                  defaultValue={
                    isEditProgram ? phoneValues.appendPhoneNumber : ""
                  }
                  placeholder={commonLabels.telephonePlaceHolder}
                  hideFormattingAndDisableValidation={isEditProgram}
                  phoneNumberFormId={ParticipantTabFormKey.appendPhoneNumber}
                  countryCodeFormId={
                    ParticipantTabFormKey.appendPhoneNumberCountryCode
                  }
                  countryIsoCodeFormId={
                    ParticipantTabFormKey.appendPhoneNumberIsoCode
                  }
                  searchPlaceholder={commonLabels.search}
                  searchNotFound={commonLabels.noResult}
                  selectLabel={commonLabels.selectPlaceHolder}
                  invalidPhoneNumberMessage={
                    isEditProgram
                      ? commonValidationMessages.phoneNumberMinimumOf3DigitsRequiredMessage
                      : commonValidationMessages.invalidPhoneNumber
                  }
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
      </MuiBox>
    </MuiBox>
  );
};
